/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useRef, useState} from 'react';
import Basemap from '@arcgis/core/Basemap';
import {get} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {BaseMapEnum} from '@ion/dls/src/types/types';
import {initializeEsriModules} from '../helpers/initializeEsriModules';

/**
 * The useBasemap hook helps to return the current basemap.
 * It loads the osm basemap from the Esri's Portal
 *
 * ```typescript
 *
 *   import {useBasemap} from './hook/tracking/useBasemap';
 *   const {loadBasemap, currentBasemap} = useBasemap();
 *
 * ```
 */
export const useBasemap = () => {
  const [currentBasemap, setCurrentBasemap] = useState(null);
  const basemapRef    = useRef(null);
  const osmBasemapRef = useRef(null);

  // Defines the Id of the Open Street Map basemap.
  const osmBasemapId = '4b50af48174c449e9488a153283b7b0e';

  const setBaseMap = useCallback((basemap: BaseMapEnum) => {
    if (basemap !== BaseMapEnum.osm) {
      setCurrentBasemap(basemap);
    } else {
      setCurrentBasemap(createOSMBasemap());
    }
  }, []);

  const loadBasemap = useCallback(async(basemap: BaseMapEnum, isFMTracking: boolean = false) => {
    if (!basemapRef.current) {
      if (!isFMTracking) {
        const [Basemap]    = await initializeEsriModules('esri/Basemap');
        basemapRef.current = Basemap;
      } else {
        basemapRef.current = Basemap;
      }
    }
    setBaseMap(basemap);
  }, [basemapRef, setBaseMap]);

  const createOSMBasemap = useCallback(() => {
    if (!osmBasemapRef.current) {
      const BasemapWidget = basemapRef.current || Basemap;
      const osmBasemap =  new BasemapWidget({
        id           : 'osm',
        title        : translate('maps:basemaps.osm'),
        thumbnailUrl : `${get('webMapDashletURL')}/${osmBasemapId}/info/thumbnail/thumbnail1547740877120.jpeg?f=json`,
        portalItem   : {
          id : osmBasemapId
        }
      });
      osmBasemapRef.current = osmBasemap;
      return osmBasemap;
    }
    return osmBasemapRef.current;
  }, [basemapRef]);

  const getCurrentBasemap = useCallback((basemap: BaseMapEnum) => {
    return basemap !== BaseMapEnum.osm ? basemap : createOSMBasemap();
  }, []);

  return {currentBasemap, createOSMBasemap, loadBasemap, getCurrentBasemap, setBaseMap};
};
export default useBasemap;
